/**
 * MultiAgentSidebarSessions — compact session list for the IDE left sidebar,
 * placed between the metadata explorer (files) and the Rules section (mirrors
 * the dev-agent sidebar order: files -> sessions -> rules). Reuses the same
 * multi-agent history source + switch logic as HistoryDrawer.
 */
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { History as HistoryIcon, Plus, Trash2 } from "lucide-react";
import {
  fetchMultiAgentRunDetail,
  invalidateMultiAgentRunsList,
  isMultiAgentAuthError,
  useRecentRuns,
} from "@/hooks/multi-agent";
import { deleteMultiAgentSession } from "@/services/multi-agent";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import {
  hydrateMultiAgentAnalysis,
  type MultiAgentRunAnalysis,
} from "@/pages/multi-agent/ide/store/analysisHydration";

export function MultiAgentSidebarSessions() {
  const queryClient = useQueryClient();
  const activeSessionId = useMultiAgentStore((s) => s.sessionId);
  const reset = useMultiAgentStore((s) => s.reset);
  const clearThread = useMultiAgentStore((s) => s.clearThread);
  const hydrateFromDetail = useMultiAgentStore((s) => s.hydrateFromDetail);
  const { sessions } = useRecentRuns({ enabled: true });

  const deleteSession = async (sessionId: string) => {
    if (!window.confirm("Delete this session and all its runs? This cannot be undone.")) return;
    try {
      const res = await deleteMultiAgentSession(sessionId);
      // If we deleted the session we're currently viewing, clear the workspace.
      if (sessionId === activeSessionId) {
        clearThread();
        reset();
        useMultiAgentIdeStore.getState().bumpNewSession();  // clear the editor's open files
      }
      await invalidateMultiAgentRunsList(queryClient);
      toast.success(`Session deleted (${res.run_count} run${res.run_count === 1 ? "" : "s"})`);
    } catch (e) {
      if (isMultiAgentAuthError(e)) {
        toast.error("Could not delete — it may belong to another organization.");
      } else {
        toast.error(`Could not delete session: ${(e as Error)?.message ?? String(e)}`);
      }
    }
  };

  const openSession = async (latestRunId: string) => {
    reset();
    useMultiAgentIdeStore.getState().bumpNewSession();  // switching sessions → clear the prior session's editor files
    try {
      const d = await fetchMultiAgentRunDetail(queryClient, latestRunId);
      hydrateFromDetail(d);
      // Rebuild the Dependencies / Impact / Test panels from the loaded run's
      // persisted analysis (the per-session-switch clear runs first via the
      // store subscription, so this repopulates with THIS session's data).
      hydrateMultiAgentAnalysis((d as { analysis?: MultiAgentRunAnalysis }).analysis);
    } catch (e) {
      if (isMultiAgentAuthError(e)) {
        toast.error("Could not open this session — it may belong to another organization.");
      } else {
        toast.error(`Could not open session: ${(e as Error)?.message ?? String(e)}`);
      }
    }
  };

  return (
    <div className="p-3">
      <div className="mb-2 flex items-center gap-2">
        <HistoryIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-foreground/85">Sessions · {sessions.length}</span>
        <button
          type="button"
          onClick={() => { clearThread(); reset(); useMultiAgentIdeStore.getState().bumpNewSession(); }}
          className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="New session"
          aria-label="New session"
        >
          <Plus className="h-3.5 w-3.5" />
          New
        </button>
      </div>
      {sessions.length === 0 ? (
        <div className="px-1 py-2 text-[11px] text-muted-foreground">No sessions yet</div>
      ) : (
        <ul className="flex flex-col gap-1">
          {sessions.slice(0, 30).map((s) => {
            const isActive = s.id === activeSessionId;
            return (
              <li key={s.id} className="group relative">
                <button
                  type="button"
                  onClick={() => openSession(s.latestRunId)}
                  className={`flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 pr-7 text-left text-xs transition-colors ${
                    isActive ? "bg-primary/10 text-foreground" : "text-foreground/90 hover:bg-muted"
                  }`}
                  title={s.title || "(no intent)"}
                >
                  <span className="line-clamp-1 w-full font-medium">{s.title || "(no intent)"}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {s.run_count} run{s.run_count === 1 ? "" : "s"} · {s.lane || "free"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void deleteSession(s.id);
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus:opacity-100"
                  title="Delete session"
                  aria-label="Delete session"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
